import type { ApiClient } from '@nly/api-client'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiClient } from '@/api'
import { formatApiError } from '@/utils/apiError'

type ScheduleListResult = Awaited<ReturnType<ApiClient['doctorSchedule']['list']>>
type OccurrenceListResult = Awaited<ReturnType<ApiClient['doctorSchedule']['occurrences']>>
type HistoryListResult = Awaited<ReturnType<ApiClient['doctorSchedule']['history']>>
type StaffOptionsResult = Awaited<ReturnType<ApiClient['doctorSchedule']['staffOptions']>>

export type DoctorSchedule = ScheduleListResult['items'][number]
export type DoctorScheduleOccurrence = OccurrenceListResult['items'][number]
export type DoctorScheduleHistory = HistoryListResult['items'][number]
export type DoctorScheduleListInput = Parameters<ApiClient['doctorSchedule']['list']>[0]
export type DoctorScheduleOccurrenceInput = Parameters<ApiClient['doctorSchedule']['occurrences']>[0]
export type DoctorScheduleHistoryInput = Parameters<ApiClient['doctorSchedule']['history']>[0]
export type DoctorScheduleCreateInput = Parameters<ApiClient['doctorSchedule']['create']>[0]
export type DoctorScheduleUpdateInput = Parameters<ApiClient['doctorSchedule']['update']>[0]
export type DoctorScheduleDisableInput = Parameters<ApiClient['doctorSchedule']['disable']>[0]
export type DoctorScheduleOccurrenceRescheduleInput = Parameters<ApiClient['doctorSchedule']['rescheduleOccurrence']>[0]
export type DoctorScheduleStaffOptionsInput = Omit<Parameters<ApiClient['doctorSchedule']['staffOptions']>[0], 'page' | 'pageSize'>
export type DoctorScheduleStaffOption = StaffOptionsResult['items'][number]

export const useDoctorScheduleStore = defineStore('doctorSchedule', () => {
  const scheduleResult = ref<ScheduleListResult | null>(null)
  const occurrenceResult = ref<OccurrenceListResult | null>(null)
  const historyResult = ref<HistoryListResult | null>(null)
  const staffOptionsResult = ref<StaffOptionsResult | null>(null)
  const scheduleLoading = ref(false)
  const occurrenceLoading = ref(false)
  const historyLoading = ref(false)
  const staffOptionsLoading = ref(false)
  const mutationLoading = ref(false)
  const scheduleError = ref<string | null>(null)
  const occurrenceError = ref<string | null>(null)
  const historyError = ref<string | null>(null)
  const staffOptionsError = ref<string | null>(null)
  let scheduleGeneration = 0
  let occurrenceGeneration = 0
  let historyGeneration = 0
  let staffOptionsGeneration = 0

  async function loadSchedules(input: DoctorScheduleListInput) {
    const request = ++scheduleGeneration
    scheduleLoading.value = true
    scheduleError.value = null
    try {
      const result = await apiClient.doctorSchedule.list(input)
      if (request !== scheduleGeneration) {
        return result
      }
      scheduleResult.value = result
      return result
    }
    catch (cause) {
      if (request === scheduleGeneration) {
        scheduleError.value = formatApiError(cause, '每周出诊安排加载失败')
      }
      throw cause
    }
    finally {
      if (request === scheduleGeneration) {
        scheduleLoading.value = false
      }
    }
  }

  async function loadAllSchedules(input: Omit<DoctorScheduleListInput, 'page' | 'pageSize'>) {
    const request = ++scheduleGeneration
    scheduleLoading.value = true
    scheduleError.value = null
    try {
      const items: ScheduleListResult['items'] = []
      let page = 1
      let total = 0
      do {
        const result = await apiClient.doctorSchedule.list({
          ...input,
          page,
          pageSize: 100,
        })
        if (request !== scheduleGeneration) {
          return result
        }
        items.push(...result.items)
        total = result.total
        if (result.items.length === 0 || items.length >= total) {
          break
        }
        page += 1
      } while (items.length < total)
      const result: ScheduleListResult = {
        items,
        page: 1,
        pageSize: 100,
        total,
      }
      if (request === scheduleGeneration) {
        scheduleResult.value = result
      }
      return result
    }
    catch (cause) {
      if (request === scheduleGeneration) {
        scheduleError.value = formatApiError(cause, '每周出诊安排加载失败')
      }
      throw cause
    }
    finally {
      if (request === scheduleGeneration) {
        scheduleLoading.value = false
      }
    }
  }
  async function loadOccurrences(input: DoctorScheduleOccurrenceInput) {
    const request = ++occurrenceGeneration
    occurrenceLoading.value = true
    occurrenceError.value = null
    try {
      const result = await apiClient.doctorSchedule.occurrences(input)
      if (request !== occurrenceGeneration) {
        return result
      }
      occurrenceResult.value = result
      return result
    }
    catch (cause) {
      if (request === occurrenceGeneration) {
        occurrenceError.value = formatApiError(cause, '具体日期出诊加载失败')
      }
      throw cause
    }
    finally {
      if (request === occurrenceGeneration) {
        occurrenceLoading.value = false
      }
    }
  }

  async function loadHistory(input: DoctorScheduleHistoryInput) {
    const request = ++historyGeneration
    historyLoading.value = true
    historyError.value = null
    try {
      const result = await apiClient.doctorSchedule.history(input)
      if (request !== historyGeneration) {
        return result
      }
      historyResult.value = result
      return result
    }
    catch (cause) {
      if (request === historyGeneration) {
        historyError.value = formatApiError(cause, '出诊历史加载失败')
      }
      throw cause
    }
    finally {
      if (request === historyGeneration) {
        historyLoading.value = false
      }
    }
  }

  async function loadStaffOptions(input: DoctorScheduleStaffOptionsInput = {}) {
    const request = ++staffOptionsGeneration
    staffOptionsLoading.value = true
    staffOptionsError.value = null
    try {
      const items: StaffOptionsResult['items'] = []
      let page = 1
      let total = 0
      do {
        const result = await apiClient.doctorSchedule.staffOptions({
          ...input,
          page,
          pageSize: 100,
        })
        if (request !== staffOptionsGeneration) {
          return result
        }
        items.push(...result.items)
        total = result.total
        if (result.items.length === 0 || items.length >= total) {
          break
        }
        page += 1
      } while (items.length < total)
      const result: StaffOptionsResult = {
        items,
        page: 1,
        pageSize: 100,
        total,
      }
      if (request === staffOptionsGeneration) {
        staffOptionsResult.value = result
      }
      return result
    }
    catch (cause) {
      if (request === staffOptionsGeneration) {
        staffOptionsError.value = formatApiError(cause, '交付部人员选项加载失败')
      }
      throw cause
    }
    finally {
      if (request === staffOptionsGeneration) {
        staffOptionsLoading.value = false
      }
    }
  }

  async function create(input: DoctorScheduleCreateInput) {
    mutationLoading.value = true
    try {
      return await apiClient.doctorSchedule.create(input)
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function update(input: DoctorScheduleUpdateInput) {
    mutationLoading.value = true
    try {
      return await apiClient.doctorSchedule.update(input)
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function disable(input: DoctorScheduleDisableInput) {
    mutationLoading.value = true
    try {
      return await apiClient.doctorSchedule.disable(input)
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function rescheduleOccurrence(input: DoctorScheduleOccurrenceRescheduleInput) {
    mutationLoading.value = true
    try {
      return await apiClient.doctorSchedule.rescheduleOccurrence(input)
    }
    finally {
      mutationLoading.value = false
    }
  }

  function clearErrors() {
    scheduleError.value = null
    occurrenceError.value = null
    historyError.value = null
    staffOptionsError.value = null
  }

  function reset() {
    scheduleGeneration++
    occurrenceGeneration++
    historyGeneration++
    staffOptionsGeneration++
    scheduleResult.value = null
    occurrenceResult.value = null
    historyResult.value = null
    staffOptionsResult.value = null
    scheduleLoading.value = false
    occurrenceLoading.value = false
    historyLoading.value = false
    staffOptionsLoading.value = false
    mutationLoading.value = false
    clearErrors()
  }

  return {
    schedules: computed(() => scheduleResult.value?.items ?? []),
    scheduleTotal: computed(() => scheduleResult.value?.total ?? 0),
    occurrences: computed(() => occurrenceResult.value?.items ?? []),
    history: computed(() => historyResult.value?.items ?? []),
    historyTotal: computed(() => historyResult.value?.total ?? 0),
    scheduleLoading: computed(() => scheduleLoading.value),
    occurrenceLoading: computed(() => occurrenceLoading.value),
    historyLoading: computed(() => historyLoading.value),
    mutationLoading: computed(() => mutationLoading.value),
    scheduleError: computed(() => scheduleError.value),
    occurrenceError: computed(() => occurrenceError.value),
    historyError: computed(() => historyError.value),
    staffOptions: computed(() => staffOptionsResult.value?.items ?? []),
    staffOptionsLoading: computed(() => staffOptionsLoading.value),
    staffOptionsError: computed(() => staffOptionsError.value),
    loadSchedules,
    loadAllSchedules,
    loadOccurrences,
    loadHistory,
    loadStaffOptions,
    create,
    update,
    disable,
    rescheduleOccurrence,
    clearErrors,
    reset,
  }
})
